import { IdeCompany, IdeCompanySearch } from "./IdeAPI";
import { SelectChooser } from "../components/select/SelectChooser";
import React from "react";
import Button from "../components/Button";
import { useI18n } from "../hooks/i18n-hooks";
import Callout from "../ui_atoms/Callout";

export type IdeChooserProps = {
    onSelect: (ideCompany: IdeCompany) => void;
    onDismiss: () => void;
    isVisible: boolean;
    language?: string;
    placeholder?: string;
};

const ideSearch = new IdeCompanySearch();

export const IdeChooser: React.FC<IdeChooserProps> = ({
    onSelect,
    onDismiss,
    isVisible,
    placeholder,
}) => {
    const i18n = useI18n();
    const searchContact = React.useCallback(
        async (filter: string) => {
            if (!filter || filter.length < 3) {
                return [];
            }
            const ret = await ideSearch.searchCompaniesByName(filter);
            const customNameItem = {
                value: {
                    name: filter,
                    uid: null,
                    address: null,
                },
                label: filter,
                labels: [{ mode: "info", text: i18n.t("custom_name") }],
            };
            const hasExactMatch = ret.some((c) => c.name?.toLowerCase() === filter.toLowerCase());
            return [
                ...ret.map((c) => ({
                    value: c,
                    label: c.name || "-",
                })),
                ...(!hasExactMatch ? [customNameItem] : []),
            ];
        },
        [i18n],
    );
    return (
        <SelectChooser<IdeCompany>
            isDynamicHeight={true}
            visible={isVisible}
            onDismiss={onDismiss}
            searchItems={searchContact}
            emptyResult={<Callout mode="warning" markdown={i18n.t("no_result")} />}
            onSelect={(i) => {
                onSelect?.(i.value);
            }}
            placeholder={placeholder}
            selectable={false}
            useDebounce={true}
        />
    );
};

type IdeChooserButtonProps = {
    onSelect?: (ideCompany: IdeCompany) => void;
    buttonLabel?: string;
    placeholder?: string;
    language?: string;
};

const IdeChooserButton: React.FC<IdeChooserButtonProps> = ({
    onSelect,
    buttonLabel,
    placeholder,
    language = "en",
}) => {
    const [isVisible, setIsVisible] = React.useState(false);

    return (
        <>
            <Button onPress={() => setIsVisible(true)} label={buttonLabel || ""} />
            <IdeChooser
                isVisible={isVisible}
                onDismiss={() => setIsVisible(false)}
                onSelect={(i) => {
                    setIsVisible(false);
                    onSelect?.(i);
                }}
                placeholder={placeholder}
                language={language}
            />
        </>
    );
};

export default IdeChooserButton;
